/*!
 * devextreme-angular-test
 * Version: 17.2.8
 * Build date: Mon Feb 05 2018
 *
 * Copyright (c) 2012 - 2018 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */











import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    Input,
    Output,
    OnDestroy,
    Injector,
    EventEmitter,
    OnInit,
    AfterViewInit,
    ContentChild,
    forwardRef,
    HostListener,
    OnChanges,
    DoCheck,
    SimpleChanges,
    ContentChildren,
    QueryList
} from '@angular/core';


import DevExpress from 'devextreme/bundles/dx.all';

import DxAutocomplete from 'devextreme/ui/autocomplete';

import { DxValidatorComponent } from './validator';

import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR
} from '@angular/forms';

import { DxComponent } from '../core/component';
import { DxTemplateHost } from '../core/template-host';
import { DxTemplateModule } from '../core/template';
import { EventsRegistrator } from '../core/events-strategy';
import { NestedOptionHost } from '../core/nested-option';
import { WatcherHelper } from '../core/watcher-helper';
import { IterableDifferHelper } from '../core/iterable-differ-helper';

import { DxiItemModule } from './nested/item-dxi';

import { DxiItemComponent } from './nested/item-dxi';




const CUSTOM_VALUE_ACCESSOR_PROVIDER = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DxAutocompleteComponent),
    multi: true
};

/**
 * The Autocomplete widget is a textbox that provides suggestions while a user types into it.
 */

export class DxAutocompleteComponent extends DxComponent implements OnDestroy, OnInit, AfterViewInit, ControlValueAccessor, OnChanges, DoCheck {
    instance: DxAutocomplete;

    
    validator: DxValidatorComponent;


    /**
     * Specifies the shortcut key that sets focus on the widget.
     */
    
    get accessKey(): string {
        return this._getOption('accessKey');
    }
    set accessKey(value: string) {
        this._setOption('accessKey', value);
    }


    /**
     * Specifies whether or not the widget changes its state when interacting with a user.
     */
    
    get activeStateEnabled(): boolean {
        return this._getOption('activeStateEnabled');
    }
    set activeStateEnabled(value: boolean) {
        this._setOption('activeStateEnabled', value);
    }


    
    get attr(): any {
        return this._getOption('attr');
    }
    set attr(value: any) {
        this._setOption('attr', value);
    }


    /**
     * A data source used to fetch data to be displayed by the widget.
     */
    
    get dataSource(): DevExpress.data.DataSource|
        DevExpress.data.DataSourceOptions|
        string|
        Array<any> {
        return this._getOption('dataSource');
    }
    set dataSource(value: DevExpress.data.DataSource|
        DevExpress.data.DataSourceOptions|
        string|
        Array<any>) {
        this._setOption('dataSource', value);
    }


    /**
     * Specifies whether widget content is rendered when the widget is shown or when rendering the widget.
     */
    
    get deferRendering(): boolean {
        return this._getOption('deferRendering');
    }
    set deferRendering(value: boolean) {
        this._setOption('deferRendering', value);
    }


    /**
     * Specifies whether the widget responds to user interaction.
     */
    
    get disabled(): boolean {
        return this._getOption('disabled');
    }
    set disabled(value: boolean) {
        this._setOption('disabled', value);
    }


    /**
     * Specifies the name of the data source item field whose value is displayed by the widget.
     */
    
    get displayExpr(): Function|
        string {
        return this._getOption('displayExpr');
    }
    set displayExpr(value: Function|
        string) {
        this._setOption('displayExpr', value);
    }


    /**
     * Returns the value currently displayed by the widget.
     */
    
    get displayValue(): string {
        return this._getOption('displayValue');
    }
    set displayValue(value: string) {
        this._setOption('displayValue', value);
    }


    /**
     * Specifies a custom template for the drop-down button.
     */
    
    get dropDownButtonTemplate(): any {
        return this._getOption('dropDownButtonTemplate');
    }
    set dropDownButtonTemplate(value: any) {
        this._setOption('dropDownButtonTemplate', value);
    }


    /**
     * Specifies the attributes to be attached to the widget's root element.
     */
    
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Specifies whether the widget can be focused using keyboard navigation.
     */
    
    get focusStateEnabled(): boolean {
        return this._getOption('focusStateEnabled');
    }
    set focusStateEnabled(value: boolean) {
        this._setOption('focusStateEnabled', value);
    }


    /**
     * Specifies whether data items should be grouped.
     */
    
    get grouped(): boolean {
        return this._getOption('grouped');
    }
    set grouped(value: boolean) {
        this._setOption('grouped', value);
    }


    /**
     * Specifies a custom template for group captions.
     */
    
    get groupTemplate(): any {
        return this._getOption('groupTemplate');
    }
    set groupTemplate(value: any) {
        this._setOption('groupTemplate', value);
    }


    /**
     * Specifies the widget's height.
     */
    
    get height(): number|
        Function|
        string {
        return this._getOption('height');
    }
    set height(value: number|
        Function|
        string) {
        this._setOption('height', value);
    }


    /**
     * Specifies text for a hint that appears when a user pauses on the widget.
     */
    
    get hint(): string {
        return this._getOption('hint');
    }
    set hint(value: string) {
        this._setOption('hint', value);
    }


    /**
     * Specifies whether the widget changes its state when a user pauses on it.
     */
    
    get hoverStateEnabled(): boolean {
        return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value: boolean) {
        this._setOption('hoverStateEnabled', value);
    }


    /**
     * Specifies the attributes to be passed on to the underlying HTML element.
     */
    
    get inputAttr(): any {
        return this._getOption('inputAttr');
    }
    set inputAttr(value: any) {
        this._setOption('inputAttr', value);
    }


    /**
     * Specifies whether the editor's value is valid.
     */
    
    get isValid(): boolean {
        return this._getOption('isValid');
    }
    set isValid(value: boolean) {
        this._setOption('isValid', value);
    }


    /**
     * An array of items displayed by the widget.
     */
    
    get items(): Array<any|
        { disabled?: boolean, html?: string, template?: any, text?: string, visible?: boolean }> {
        return this._getOption('items');
    }
    set items(value: Array<any|
        { disabled?: boolean, html?: string, template?: any, text?: string, visible?: boolean }>) {
        this._setOption('items', value);
    }


    /**
     * Specifies a custom template for an item.
     */
    
    get itemTemplate(): any {
        return this._getOption('itemTemplate');
    }
    set itemTemplate(value: any) {
        this._setOption('itemTemplate', value);
    }


    /**
     * Specifies the maximum count of items displayed by the widget.
     */
    
    get maxItemCount(): number {
        return this._getOption('maxItemCount');
    }
    set maxItemCount(value: number) {
        this._setOption('maxItemCount', value);
    }


    /**
     * Specifies the maximum number of characters you can enter into the textbox.
     */
    
    get maxLength(): number|
        string {
        return this._getOption('maxLength');
    }
    set maxLength(value: number|
        string) {
        this._setOption('maxLength', value);
    }


    /**
     * The minimum number of characters that must be entered into the text box to begin a search.
     */
    
    get minSearchLength(): number {
        return this._getOption('minSearchLength');
    }
    set minSearchLength(value: number) {
        this._setOption('minSearchLength', value);
    }


    /**
     * The value to be assigned to the `name` attribute of the underlying HTML element.
     */
    
    get name(): string {
        return this._getOption('name');
    }
    set name(value: string) {
        this._setOption('name', value);
    }


    /**
     * Specifies whether or not the drop-down editor is displayed.
     */
    
    get opened(): boolean {
        return this._getOption('opened');
    }
    set opened(value: boolean) {
        this._setOption('opened', value);
    }


    /**
     * The text displayed by the widget when the widget value is empty.
     */
    
    get placeholder(): string {
        return this._getOption('placeholder');
    }
    set placeholder(value: string) {
        this._setOption('placeholder', value);
    }


    /**
     * A Boolean value specifying whether or not the widget is read-only.
     */
    
    get readOnly(): boolean {
        return this._getOption('readOnly');
    }
    set readOnly(value: boolean) {
        this._setOption('readOnly', value);
    }


    /**
     * Switches the widget to a right-to-left representation.
     */
    
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * Specifies the name of a data source item field or an expression whose value is compared to the search criterion.
     */
    
    get searchExpr(): Function|
        Array<Function> {
        return this._getOption('searchExpr');
    }
    set searchExpr(value: Function|
        Array<Function>) {
        this._setOption('searchExpr', value);
    }


    /**
     * Specifies the binary operation used to filter data.
     */
    
    get searchMode(): string {
        return this._getOption('searchMode');
    }
    set searchMode(value: string) {
        this._setOption('searchMode', value);
    }


    /**
     * Specifies the time delay, in milliseconds, after the last character has been typed in, before a search is executed.
     */
    
    get searchTimeout(): number {
        return this._getOption('searchTimeout');
    }
    set searchTimeout(value: number) {
        this._setOption('searchTimeout', value);
    }


    /**
     * Gets the currently selected item.
     */
    
    get selectedItem(): any {
        return this._getOption('selectedItem');
    }
    set selectedItem(value: any) {
        this._setOption('selectedItem', value);
    }


    /**
     * Specifies whether to display the Clear button in the widget.
     */
    
    get showClearButton(): boolean {
        return this._getOption('showClearButton');
    }
    set showClearButton(value: boolean) {
        this._setOption('showClearButton', value);
    }


    /**
     * Specifies whether or not the widget checks the inner text for spelling mistakes.
     */
    
    get spellcheck(): boolean {
        return this._getOption('spellcheck');
    }
    set spellcheck(value: boolean) {
        this._setOption('spellcheck', value);
    }


    /**
     * Specifies the number of the element when the Tab key is used for navigating.
     */
    
    get tabIndex(): number {
        return this._getOption('tabIndex');
    }
    set tabIndex(value: number) {
        this._setOption('tabIndex', value);
    }


    /**
     * The read-only option that holds the text displayed by the widget input element.
     */
    
    get text(): string {
        return this._getOption('text');
    }
    set text(value: string) {
        this._setOption('text', value);
    }


    /**
     * Specifies information on the validation error when using a custom validation engine. Should be changed at runtime along with the isValid option.
     */
    
    get validationError(): any {
        return this._getOption('validationError');
    }
    set validationError(value: any) {
        this._setOption('validationError', value);
    }


    /**
     * Specifies how the message about the validation rules that are not satisfied by this editor's value is displayed.
     */
    
    get validationMessageMode(): string {
        return this._getOption('validationMessageMode');
    }
    set validationMessageMode(value: string) {
        this._setOption('validationMessageMode', value);
    }


    /**
     * Specifies the current value displayed by the widget.
     */
    
    get value(): string {
        return this._getOption('value');
    }
    set value(value: string) {
        this._setOption('value', value);
    }


    /**
     * Specifies DOM event names that update a widget's value.
     */
    
    get valueChangeEvent(): string {
        return this._getOption('valueChangeEvent');
    }
    set valueChangeEvent(value: string) {
        this._setOption('valueChangeEvent', value);
    }


    /**
     * Specifies which data field provides the widget's value. When this option is not set, the value is the entire data object.
     */
    
    get valueExpr(): Function|
        string {
        return this._getOption('valueExpr');
    }
    set valueExpr(value: Function|
        string) {
        this._setOption('valueExpr', value);
    }


    /**
     * Specifies whether the widget is visible.
     */
    
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the widget's width.
     */
    
    get width(): number|
        Function|
        string {
        return this._getOption('width');
    }
    set width(value: number|
        Function|
        string) {
        this._setOption('width', value);
    }

    /**
     * A handler for the change event.
     */
     onChange: EventEmitter<any>;

    /**
     * A handler for the closed event.
     */
     onClosed: EventEmitter<any>;

    /**
     * A handler for the contentReady event. Executed when the widget's content is ready. This handler may be executed multiple times during the widget's lifetime depending on the number of times its content changes.
     */
     onContentReady: EventEmitter<any>;

    /**
     * A handler for the copy event.
     */
     onCopy: EventEmitter<any>;

    /**
     * A handler for the cut event.
     */
     onCut: EventEmitter<any>;

    /**
     * A handler for the disposing event. Executed when the widget is removed from the DOM using the remove(), empty(), or html() jQuery methods only.
     */
     onDisposing: EventEmitter<any>;

    /**
     * A handler for the enterKey event.
     */
     onEnterKey: EventEmitter<any>;

    /**
     * A handler for the focusIn event.
     */
     onFocusIn: EventEmitter<any>;

    /**
     * A handler for the focusOut event.
     */
     onFocusOut: EventEmitter<any>;

    /**
     * A handler for the initialized event. Executed only once, after the widget is initialized.
     */
     onInitialized: EventEmitter<any>;

    /**
     * A handler for the input event.
     */
     onInput: EventEmitter<any>;

    /**
     * A handler for the itemClick event.
     */
     onItemClick: EventEmitter<any>;

    /**
     * A handler for the keyDown event.
     */
     onKeyDown: EventEmitter<any>;

    /**
     * A handler for the keyPress event.
     */
     onKeyPress: EventEmitter<any>;

    /**
     * A handler for the keyUp event.
     */
     onKeyUp: EventEmitter<any>;

    /**
     * A handler for the opened event.
     */
     onOpened: EventEmitter<any>;

    /**
     * A handler for the optionChanged event. Executed after an option of the widget is changed.
     */
     onOptionChanged: EventEmitter<any>;

    /**
     * A handler for the paste event.
     */
     onPaste: EventEmitter<any>;

    /**
     * A handler for the selectionChanged event.
     */
     onSelectionChanged: EventEmitter<any>;

    /**
     * A handler for the valueChanged event.
     */
     onValueChanged: EventEmitter<any>;

    /**
     * A handler for the accessKeyChange event.
     */
     accessKeyChange: EventEmitter<string>;

    /**
     * A handler for the activeStateEnabledChange event.
     */
     activeStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the attrChange event.
     */
     attrChange: EventEmitter<any>;

    /**
     * A handler for the dataSourceChange event.
     */
     dataSourceChange: EventEmitter<DevExpress.data.DataSource|
        DevExpress.data.DataSourceOptions|
        string|
        Array<any>>;

    /**
     * A handler for the deferRenderingChange event.
     */
     deferRenderingChange: EventEmitter<boolean>;

    /**
     * A handler for the disabledChange event.
     */
     disabledChange: EventEmitter<boolean>;

    /**
     * A handler for the displayExprChange event.
     */
     displayExprChange: EventEmitter<Function|
        string>;

    /**
     * A handler for the displayValueChange event.
     */
     displayValueChange: EventEmitter<string>;

    /**
     * A handler for the dropDownButtonTemplateChange event.
     */
     dropDownButtonTemplateChange: EventEmitter<any>;

    /**
     * A handler for the elementAttrChange event.
     */
     elementAttrChange: EventEmitter<any>;

    /**
     * A handler for the focusStateEnabledChange event.
     */
     focusStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the groupedChange event.
     */
     groupedChange: EventEmitter<boolean>;

    /**
     * A handler for the groupTemplateChange event.
     */
     groupTemplateChange: EventEmitter<any>;

    /**
     * A handler for the heightChange event.
     */
     heightChange: EventEmitter<number|
        Function|
        string>;

    /**
     * A handler for the hintChange event.
     */
     hintChange: EventEmitter<string>;

    /**
     * A handler for the hoverStateEnabledChange event.
     */
     hoverStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the inputAttrChange event.
     */
     inputAttrChange: EventEmitter<any>;

    /**
     * A handler for the isValidChange event.
     */
     isValidChange: EventEmitter<boolean>;

    /**
     * A handler for the itemsChange event.
     */
     itemsChange: EventEmitter<Array<any|
        { disabled?: boolean, html?: string, template?: any, text?: string, visible?: boolean }>>;

    /**
     * A handler for the itemTemplateChange event.
     */
     itemTemplateChange: EventEmitter<any>;

    /**
     * A handler for the maxItemCountChange event.
     */
     maxItemCountChange: EventEmitter<number>;

    /**
     * A handler for the maxLengthChange event.
     */
     maxLengthChange: EventEmitter<number|
        string>;

    /**
     * A handler for the minSearchLengthChange event.
     */
     minSearchLengthChange: EventEmitter<number>;

    /**
     * A handler for the nameChange event.
     */
     nameChange: EventEmitter<string>;

    /**
     * A handler for the openedChange event.
     */
     openedChange: EventEmitter<boolean>;

    /**
     * A handler for the placeholderChange event.
     */
     placeholderChange: EventEmitter<string>;

    /**
     * A handler for the readOnlyChange event.
     */
     readOnlyChange: EventEmitter<boolean>;

    /**
     * A handler for the rtlEnabledChange event.
     */
     rtlEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the searchExprChange event.
     */
     searchExprChange: EventEmitter<Function|
        Array<Function>>;

    /**
     * A handler for the searchModeChange event.
     */
     searchModeChange: EventEmitter<string>;

    /**
     * A handler for the searchTimeoutChange event.
     */
     searchTimeoutChange: EventEmitter<number>;

    /**
     * A handler for the selectedItemChange event.
     */
     selectedItemChange: EventEmitter<any>;

    /**
     * A handler for the showClearButtonChange event.
     */
     showClearButtonChange: EventEmitter<boolean>;

    /**
     * A handler for the spellcheckChange event.
     */
     spellcheckChange: EventEmitter<boolean>;

    /**
     * A handler for the tabIndexChange event.
     */
     tabIndexChange: EventEmitter<number>;

    /**
     * A handler for the textChange event.
     */
     textChange: EventEmitter<string>;

    /**
     * A handler for the validationErrorChange event.
     */
     validationErrorChange: EventEmitter<any>;

    /**
     * A handler for the validationMessageModeChange event.
     */
     validationMessageModeChange: EventEmitter<string>;

    /**
     * A handler for the valueChange event.
     */
     valueChange: EventEmitter<string>;

    /**
     * A handler for the valueChangeEventChange event.
     */
     valueChangeEventChange: EventEmitter<string>;

    /**
     * A handler for the valueExprChange event.
     */
     valueExprChange: EventEmitter<Function|
        string>;

    /**
     * A handler for the visibleChange event.
     */
     visibleChange: EventEmitter<boolean>;

    /**
     * A handler for the widthChange event.
     */
     widthChange: EventEmitter<number|
        Function|
        string>;

     onBlur: EventEmitter<any>;


     change(_) { }
     touched = () => {};


    
    get itemsChildren(): QueryList<DxiItemComponent> {
        return this._getOption('items');
    }
    set itemsChildren(value) {
        this.setChildren('items', value);
    }


    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost, injector: Injector,
            private _watcherHelper: WatcherHelper,
            private _idh: IterableDifferHelper, optionHost: NestedOptionHost) {

        super(elementRef, ngZone, templateHost, _watcherHelper);
        injector.get(EventsRegistrator);

        this._createEventEmitters([
            { subscribe: 'change', emit: 'onChange' },
            { subscribe: 'closed', emit: 'onClosed' },
            { subscribe: 'contentReady', emit: 'onContentReady' },
            { subscribe: 'copy', emit: 'onCopy' },
            { subscribe: 'cut', emit: 'onCut' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'enterKey', emit: 'onEnterKey' },
            { subscribe: 'focusIn', emit: 'onFocusIn' },
            { subscribe: 'focusOut', emit: 'onFocusOut' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'input', emit: 'onInput' },
            { subscribe: 'itemClick', emit: 'onItemClick' },
            { subscribe: 'keyDown', emit: 'onKeyDown' },
            { subscribe: 'keyPress', emit: 'onKeyPress' },
            { subscribe: 'keyUp', emit: 'onKeyUp' },
            { subscribe: 'opened', emit: 'onOpened' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { subscribe: 'paste', emit: 'onPaste' },
            { subscribe: 'selectionChanged', emit: 'onSelectionChanged' },
            { subscribe: 'valueChanged', emit: 'onValueChanged' },
            { emit: 'accessKeyChange' },
            { emit: 'activeStateEnabledChange' },
            { emit: 'attrChange' },
            { emit: 'dataSourceChange' },
            { emit: 'deferRenderingChange' },
            { emit: 'disabledChange' },
            { emit: 'displayExprChange' },
            { emit: 'displayValueChange' },
            { emit: 'dropDownButtonTemplateChange' },
            { emit: 'elementAttrChange' },
            { emit: 'focusStateEnabledChange' },
            { emit: 'groupedChange' },
            { emit: 'groupTemplateChange' },
            { emit: 'heightChange' },
            { emit: 'hintChange' },
            { emit: 'hoverStateEnabledChange' },
            { emit: 'inputAttrChange' },
            { emit: 'isValidChange' },
            { emit: 'itemsChange' },
            { emit: 'itemTemplateChange' },
            { emit: 'maxItemCountChange' },
            { emit: 'maxLengthChange' },
            { emit: 'minSearchLengthChange' },
            { emit: 'nameChange' },
            { emit: 'openedChange' },
            { emit: 'placeholderChange' },
            { emit: 'readOnlyChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'searchExprChange' },
            { emit: 'searchModeChange' },
            { emit: 'searchTimeoutChange' },
            { emit: 'selectedItemChange' },
            { emit: 'showClearButtonChange' },
            { emit: 'spellcheckChange' },
            { emit: 'tabIndexChange' },
            { emit: 'textChange' },
            { emit: 'validationErrorChange' },
            { emit: 'validationMessageModeChange' },
            { emit: 'valueChange' },
            { emit: 'valueChangeEventChange' },
            { emit: 'valueExprChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' },
            { emit: 'onBlur' }
        ]);

        this._idh.setHost(this);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {
        return new DxAutocomplete(element, options);
    }

    writeValue(value: any): void {
        this.value = value;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    registerOnChange(fn: (_: any) => void): void { this.change = fn; }
    registerOnTouched(fn: () => void): void { this.touched = fn; }

    _createWidget(element: any) {
        super._createWidget(element);
        this.instance.on('focusOut', (e) => {
            this.eventHelper.fireNgEvent('onBlur', [e]);
        });
    }

    ngOnDestroy() {
        this._destroyWidget();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.setupChanges('dataSource', changes);
        this.setupChanges('items', changes);
        this.setupChanges('searchExpr', changes);
    }

    setupChanges(prop: string, changes: SimpleChanges) {
        if (!(prop in this._optionsToUpdate)) {
            this._idh.setup(prop, changes);
        }
    }

    ngDoCheck() {
        this._idh.doCheck('dataSource');
        this._idh.doCheck('items');
        this._idh.doCheck('searchExpr');
        this._watcherHelper.checkWatchers();
        super.ngDoCheck();
    }

    _setOption(name: string, value: any) {
        let isSetup = this._idh.setupSingle(name, value);
        let isChanged = this._idh.getChanges(name, value) !== null;

        if (isSetup || isChanged) {
            super._setOption(name, value);
        }
    }

    ngOnInit() {
        super.ngOnInit();
        if (this.validator) {
            this.validator.createInstanceOnInit = false;
        }
    }
    ngAfterViewInit() {
        super.ngAfterViewInit();
        if (this.validator) {
            this.validator.createInstance(this.element.nativeElement);
        }
    }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'dx-autocomplete',
    template: '',
    providers: [
        DxTemplateHost,
        WatcherHelper,
        CUSTOM_VALUE_ACCESSOR_PROVIDER,
        NestedOptionHost,
        IterableDifferHelper
    ]
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: NgZone, },
{type: DxTemplateHost, },
{type: Injector, },
{type: WatcherHelper, },
{type: IterableDifferHelper, },
{type: NestedOptionHost, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'validator': [{ type: ContentChild, args: [DxValidatorComponent, ] },],
'accessKey': [{ type: Input },],
'activeStateEnabled': [{ type: Input },],
'attr': [{ type: Input },],
'dataSource': [{ type: Input },],
'deferRendering': [{ type: Input },],
'disabled': [{ type: Input },],
'displayExpr': [{ type: Input },],
'displayValue': [{ type: Input },],
'dropDownButtonTemplate': [{ type: Input },],
'elementAttr': [{ type: Input },],
'focusStateEnabled': [{ type: Input },],
'grouped': [{ type: Input },],
'groupTemplate': [{ type: Input },],
'height': [{ type: Input },],
'hint': [{ type: Input },],
'hoverStateEnabled': [{ type: Input },],
'inputAttr': [{ type: Input },],
'isValid': [{ type: Input },],
'items': [{ type: Input },],
'itemTemplate': [{ type: Input },],
'maxItemCount': [{ type: Input },],
'maxLength': [{ type: Input },],
'minSearchLength': [{ type: Input },],
'name': [{ type: Input },],
'opened': [{ type: Input },],
'placeholder': [{ type: Input },],
'readOnly': [{ type: Input },],
'rtlEnabled': [{ type: Input },],
'searchExpr': [{ type: Input },],
'searchMode': [{ type: Input },],
'searchTimeout': [{ type: Input },],
'selectedItem': [{ type: Input },],
'showClearButton': [{ type: Input },],
'spellcheck': [{ type: Input },],
'tabIndex': [{ type: Input },],
'text': [{ type: Input },],
'validationError': [{ type: Input },],
'validationMessageMode': [{ type: Input },],
'value': [{ type: Input },],
'valueChangeEvent': [{ type: Input },],
'valueExpr': [{ type: Input },],
'visible': [{ type: Input },],
'width': [{ type: Input },],
'onChange': [{ type: Output },],
'onClosed': [{ type: Output },],
'onContentReady': [{ type: Output },],
'onCopy': [{ type: Output },],
'onCut': [{ type: Output },],
'onDisposing': [{ type: Output },],
'onEnterKey': [{ type: Output },],
'onFocusIn': [{ type: Output },],
'onFocusOut': [{ type: Output },],
'onInitialized': [{ type: Output },],
'onInput': [{ type: Output },],
'onItemClick': [{ type: Output },],
'onKeyDown': [{ type: Output },],
'onKeyPress': [{ type: Output },],
'onKeyUp': [{ type: Output },],
'onOpened': [{ type: Output },],
'onOptionChanged': [{ type: Output },],
'onPaste': [{ type: Output },],
'onSelectionChanged': [{ type: Output },],
'onValueChanged': [{ type: Output },],
'accessKeyChange': [{ type: Output },],
'activeStateEnabledChange': [{ type: Output },],
'attrChange': [{ type: Output },],
'dataSourceChange': [{ type: Output },],
'deferRenderingChange': [{ type: Output },],
'disabledChange': [{ type: Output },],
'displayExprChange': [{ type: Output },],
'displayValueChange': [{ type: Output },],
'dropDownButtonTemplateChange': [{ type: Output },],
'elementAttrChange': [{ type: Output },],
'focusStateEnabledChange': [{ type: Output },],
'groupedChange': [{ type: Output },],
'groupTemplateChange': [{ type: Output },],
'heightChange': [{ type: Output },],
'hintChange': [{ type: Output },],
'hoverStateEnabledChange': [{ type: Output },],
'inputAttrChange': [{ type: Output },],
'isValidChange': [{ type: Output },],
'itemsChange': [{ type: Output },],
'itemTemplateChange': [{ type: Output },],
'maxItemCountChange': [{ type: Output },],
'maxLengthChange': [{ type: Output },],
'minSearchLengthChange': [{ type: Output },],
'nameChange': [{ type: Output },],
'openedChange': [{ type: Output },],
'placeholderChange': [{ type: Output },],
'readOnlyChange': [{ type: Output },],
'rtlEnabledChange': [{ type: Output },],
'searchExprChange': [{ type: Output },],
'searchModeChange': [{ type: Output },],
'searchTimeoutChange': [{ type: Output },],
'selectedItemChange': [{ type: Output },],
'showClearButtonChange': [{ type: Output },],
'spellcheckChange': [{ type: Output },],
'tabIndexChange': [{ type: Output },],
'textChange': [{ type: Output },],
'validationErrorChange': [{ type: Output },],
'validationMessageModeChange': [{ type: Output },],
'valueChange': [{ type: Output },],
'valueChangeEventChange': [{ type: Output },],
'valueExprChange': [{ type: Output },],
'visibleChange': [{ type: Output },],
'widthChange': [{ type: Output },],
'onBlur': [{ type: Output },],
'change': [{ type: HostListener, args: ['valueChange', ['$event'], ] },],
'touched': [{ type: HostListener, args: ['onBlur', ['$event'], ] },],
'itemsChildren': [{ type: ContentChildren, args: [DxiItemComponent, ] },],
};
}


export class DxAutocompleteModule { static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  imports: [
    DxiItemModule,
    DxTemplateModule
  ],
  declarations: [
    DxAutocompleteComponent
  ],
  exports: [
    DxAutocompleteComponent,
    DxiItemModule,
    DxTemplateModule
  ],
  providers: [EventsRegistrator]

}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
